import React, { useState, useEffect } from 'react';
import { aiAPI } from '../../../services/api';
import { Button, Card } from '../../../components/ui';
import toast from 'react-hot-toast';

const CONTENT_TYPES = [
  { value: 'email', label: '📧 Email', desc: 'Professional outreach / follow-up email' },
  { value: 'whatsapp', label: '💬 WhatsApp', desc: 'Short conversational message' },
  { value: 'proposal_intro', label: '📋 Proposal', desc: 'Executive partnership proposal' },
  { value: 'objection_handler', label: '🛡 Objection Handler', desc: 'Rebuttals to common objections' },
  { value: 'mou_followup', label: '📜 MoU Follow-up', desc: 'Formal MoU reminder & next steps' },
];

// What content type makes most sense at each deal stage
const STAGE_RECOMMENDATIONS = {
  'New':                { type: 'email',            reason: 'First contact — a cold outreach email is best to introduce Nova Era Labs.' },
  'Contacted':          { type: 'whatsapp',         reason: 'Already reached out — a short WhatsApp nudge keeps it casual and gets a reply faster.' },
  'Interested':         { type: 'proposal_intro',   reason: 'They\'re interested — now is the time to send a formal proposal.' },
  'Proposal Sent':      { type: 'objection_handler',reason: 'Proposal is out — prepare for objections about fee, program credibility, or batch schedule.' },
  'Negotiation':        { type: 'objection_handler',reason: 'In negotiation — address fee or terms objections with confidence.' },
  'Partnership Closed': { type: 'mou_followup',     reason: 'Partnership closed — follow up on MoU signing and onboarding next steps.' },
  'Lost':               { type: 'email',            reason: 'Deal lost — a re-engagement email after some time can revive the conversation.' },
};

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };

export default function AIStudioTab({ lead }) {
  const recommendation = STAGE_RECOMMENDATIONS[lead.stage];
  const [contentType, setContentType] = useState(recommendation?.type || 'email');
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState('');
  const [aiCommsLoading, setAiCommsLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sentiment, setSentiment] = useState(lead.ai_sentiment || null);

  // When stage changes (lead prop updates), update the recommended type
  useEffect(() => {
    if (recommendation) setContentType(recommendation.type);
  }, [lead.stage]);

  const generate = async () => {
    setGenerating(true);
    setGenerated('');
    try {
      const res = await aiAPI.generateContent({
        lead_id: lead._id,
        content_type: contentType,
        context,
      });
      setGenerated(res.data?.data?.content || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  // Let AI decide both the content type AND the content based on full deal history
  const generateAiChoice = async () => {
    setAiCommsLoading(true);
    setGenerated('');
    try {
      const res = await aiAPI.tabAssist({ lead_id: lead._id, tab: 'comms' });
      const data = res.data?.data;
      if (data) {
        if (data.recommended_type) setContentType(data.recommended_type);
        const content = [
          data.reason_for_recommendation ? `[AI chose this because: ${data.reason_for_recommendation}]\n` : '',
          data.subject ? `Subject: ${data.subject}\n\n` : '',
          data.content || ''
        ].join('');
        setGenerated(content);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI generation failed');
    } finally { setAiCommsLoading(false); }
  };

  const analyzeSentiment = async () => {
    setAnalyzing(true);
    try {
      const res = await aiAPI.analyzeSentiment(lead._id);
      setSentiment(res.data?.data);
      toast.success('Sentiment analyzed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generated);
    toast.success('Copied to clipboard');
  };

  const sentimentColor = { Positive: '#16a34a', Neutral: '#d97706', Negative: '#dc2626', Mixed: '#7c3aed' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Left: Content Generator */}
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>AI Communication Studio</h3>

        {/* Stage-aware recommendation banner */}
        {recommendation && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 2 }}>💡 RECOMMENDED FOR "{lead.stage}" STAGE</p>
            <p style={{ fontSize: 12, color: '#166534' }}>{recommendation.reason}</p>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 8 }}>Content Type</label>
              <div style={{ display: 'grid', gap: 6 }}>
                {CONTENT_TYPES.map(ct => (
                  <label key={ct.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1.5px solid ${contentType === ct.value ? '#1B2A6B' : '#e2e8f0'}`, borderRadius: 8, cursor: 'pointer', background: contentType === ct.value ? '#e0e7ff' : '#fff', transition: 'all 0.15s' }}>
                    <input type="radio" name="ctype" value={ct.value} checked={contentType === ct.value} onChange={() => setContentType(ct.value)} style={{ display: 'none' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: contentType === ct.value ? '#1B2A6B' : '#334155' }}>{ct.label}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{ct.desc}</span>
                    {recommendation?.type === ct.value && (
                      <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '1px 6px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap' }}>Recommended</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                Additional Context <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — e.g. "they objected to the fee", "MoU was shared on 10th March")</span>
              </label>
              <textarea value={context} onChange={e => setContext(e.target.value)} style={{ ...inp, minHeight: 70, resize: 'vertical' }} placeholder="Add any specific details for this message…" />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Button variant="gold" onClick={generate} disabled={generating || aiCommsLoading} style={{ flex: 1, justifyContent: 'center' }}>
                    {generating ? '⚡ Generating…' : '⚡ Generate'}
                  </Button>
                  <span title="Generate the selected content type for this lead" style={{ fontSize: 14, color: '#94a3b8', cursor: 'help', userSelect: 'none' }}>ℹ️</span>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Button variant="outline" onClick={generateAiChoice} disabled={generating || aiCommsLoading} style={{ flex: 1, justifyContent: 'center' }}>
                    {aiCommsLoading ? '✨ Thinking…' : '✨ Let AI Decide'}
                  </Button>
                  <span title="Claude analyzes this lead's stage, sentiment, and last interaction to choose the most effective content type automatically" style={{ fontSize: 14, color: '#94a3b8', cursor: 'help', userSelect: 'none' }}>ℹ️</span>
                </div>
                <p style={{ fontSize: 10, color: '#94a3b8', margin: 0, textAlign: 'center' }}>
                  Best content type is auto-selected based on lead activity
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Sentiment Analysis */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B' }}>AI Sentiment Analysis</h4>
            <Button size="sm" variant="outline" onClick={analyzeSentiment} disabled={analyzing}>
              {analyzing ? 'Analyzing…' : '🔍 Analyze'}
            </Button>
          </div>
          {sentiment ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>
                  {sentiment.sentiment === 'Positive' ? '😊' : sentiment.sentiment === 'Negative' ? '😟' : sentiment.sentiment === 'Mixed' ? '🤔' : '😐'}
                </span>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: sentimentColor[sentiment.sentiment] || '#64748b' }}>{sentiment.sentiment}</p>
                  <p style={{ fontSize: 11, color: '#94a3b8' }}>Confidence: {Math.round((sentiment.confidence || 0) * 100)}%</p>
                </div>
              </div>
              {sentiment.summary && <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{sentiment.summary}</p>}
              {sentiment.key_signals?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>KEY SIGNALS</p>
                  {sentiment.key_signals.map((s, i) => (
                    <span key={i} style={{ display: 'inline-block', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 12, fontSize: 11, marginRight: 4, marginBottom: 4 }}>{s}</span>
                  ))}
                </div>
              )}
              {sentiment.recommended_action && (
                <div style={{ marginTop: 8, padding: 10, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>💡 Recommended Action</p>
                  <p style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>{sentiment.recommended_action}</p>
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
              Click Analyze to get AI-powered sentiment insights based on lead notes and activity.
            </p>
          )}
        </Card>
      </div>

      {/* Right: Generated Output */}
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Generated Content</h3>
        <Card style={{ height: 'calc(100% - 36px)' }}>
          {generated ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Button size="sm" variant="ghost" onClick={copyToClipboard}>📋 Copy</Button>
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#334155', lineHeight: 1.6, margin: 0 }}>
                {generated}
              </pre>
            </>
          ) : (generating || aiCommsLoading) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Claude is crafting your content…</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94a3b8', gap: 8 }}>
              <span style={{ fontSize: 40 }}>✨</span>
              <p style={{ fontSize: 13 }}>Select content type and click Generate</p>
              <p style={{ fontSize: 12 }}>Or use "Let AI Decide" to auto-generate based on deal history</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
